<template>
  <div>
    <draggable
      v-model="tasks.children"
      group="task"
      ghost-class="pa-gantt__task--ghost"
      :animation="200"
      handle=".pa-gantt__task--handle"
      @start="dragTask = true"
      @end="dragTask = false"
      @add="onTaskMove"
      @remove="onTaskMove"
      @update="onTaskMove"
    >
      <transition-group
        type="transition"
        :name="!dragTask ? 'flip-list' : null"
      >
        <div
          v-for="task in tasks.children"
          :key="`task-${task.id}`"
        >
          <v-list
            class="py-0"
          >
            <v-list-item
              class="pa-gantt__task"
              :class="{ 'v-list-item--active': selectedTasks.includes(task.id) }"
              @click.exact="$emit('click', task)"
              @click.ctrl.exact="$emit('click-more', task)"
            >
              <v-list-item-icon class="my-3 mr-0">
                <v-icon class="pa-gantt__task--handle">
                  drag_indicator
                </v-icon>
              </v-list-item-icon>

              <v-icon
                v-if="task.children && task.children.length && !groupBy"
                class="pa-gantt__task__toggle"
                :class="{ 'pa-gantt__task__toggle--expanded': task.expanded }"
                @click.stop="$emit('expanded'); task.expanded = !task.expanded"
              >
                arrow_right
              </v-icon>

              <v-list-item-content
                :style="!task.children || !task.children.length || groupBy ? 'padding-left:24px' : ''"
              >
                <v-list-item-title class="d-flex">
                  <span
                    :class="{ 'pa-gantt__task--completed': task.progress === 100 }"
                  >
                    {{ getProperty(task, groupBy ? groupName : taskName) }}
                  </span>

                  <v-spacer />

                  <span
                    v-if="task.childrenCount && task.childrenCount[1]"
                    class="caption mx-2"
                  >
                    {{ task.childrenCount[0] }} / {{ task.childrenCount[1] }}
                  </span>

                  <pa-user-avatar
                    v-if="task.owner"
                    :user-id="task.owner"
                    :size="20"
                    class="ma-0"
                  />

                  <span class="caption ml-2">
                    {{ getProperty(task, taskStart) | fmtDatesInterval(getProperty(task, taskEnd)) }}
                  </span>
                </v-list-item-title>
              </v-list-item-content>

              <v-list-item-icon
                class="pa-gantt__task__buttons my-3"
              >
                <v-icon @click.stop="$emit('edit-task', task)">
                  edit
                </v-icon>
              </v-list-item-icon>
            </v-list-item>
          </v-list>

          <!-- Children container -->
          <v-expand-transition
            v-if="task.children && task.children.length && !groupBy"
            :key="`childrenof-${task.id}`"
          >
            <div
              v-show="task.expanded"
              style="padding-left:24px;"
            >
              <pa-gantt-tasks
                :tasks="task"
                :group-by="groupBy"
                :group-name="groupName"
                :selected-tasks="selectedTasks"
                :task="task"
                :task-start="taskStart"
                :task-end="taskEnd"
                :task-duration="taskDuration"
                :task-name="taskName"
                @click="reEmit('click', $event)"
                @click-more="reEmit('click-more', $event)"
                @add-task="reEmit('add-task', $event)"
                @edit-task="reEmit('edit-task', $event)"
                @expanded="reEmit('expanded', $event)"
              />
            </div>
          </v-expand-transition>
        </div>
      </transition-group>
    </draggable>

    <!-- Add task button -->
    <v-btn
      block
      text
      small
      @click="$emit('add-task', { parentId: tasks.id, order: tasks.children.length })"
    >
      <v-icon left>
        add
      </v-icon>

      {{ `${$t('add')} ${$tc('activities._', 1)}` }}
    </v-btn>
  </div>
</template>

<script>
import draggable from 'vuedraggable'

import { getProperty } from '@/utils'

export default {
  name: 'PaGanttTasks',
  components: {
    draggable
  },
  props: {
    groupBy: {
      type: String,
      default: null
    },
    groupName: {
      type: String,
      default: 'group'
    },
    selectedTasks: {
      type: Array,
      default: () => []
    },
    tasks: {
      type: Object,
      required: true
    },
    taskDuration: {
      type: String,
      default: 'duration'
    },
    taskEnd: {
      type: String,
      default: 'dueDate'
    },
    taskStart: {
      type: String,
      default: 'startDate'
    },
    taskName: {
      type: String,
      default: 'title'
    }
  },
  data () {
    return {
      dragTask: false
    }
  },
  methods: {
    getProperty,
    onTaskMove (event) {
      let minIndex
      let maxIndex

      if (event.type === 'update') {
        // Task moved inside the same branch, so the parent is the same
        minIndex = Math.min(event.oldIndex, event.newIndex)
        maxIndex = Math.max(event.oldIndex, event.newIndex)
      } else if (event.type === 'add') {
        // Task moved to another branch, so parent is different
        minIndex = event.newIndex
        maxIndex = this.tasks.children.length - 1
      } else if (event.type === 'remove') {
        // Task moved to another branch, so parent is different
        minIndex = event.oldIndex
        maxIndex = this.tasks.children.length - 1
      }

      for (let i = minIndex; i <= maxIndex; i++) {
        const movedTask = this.tasks.children[i]
        if (movedTask.parentId !== this.tasks.id) {
          movedTask.parentId = this.tasks.id
        }
        movedTask.order = i
        movedTask.save({ skipLog: true })
      }
    },
    reEmit (name, event) {
      this.$emit(name, event)
    }
  }
}
</script>

<style lang="scss">
@import "~styles/_vars.scss";

.pa-gantt__task {
  &--completed {
    text-decoration: line-through;
    opacity: 0.7;
  }

  &:hover {
    .pa-gantt__task--handle {
      visibility: visible;
    }

    .pa-gantt__task__buttons {
      visibility: visible;
    }
  }

  &--handle,
  &__toggle {
    overflow: visible;

    &--expanded {
      transform: rotateZ(90deg);
    }
  }

  &--handle {
    cursor: move;
    visibility: hidden;
    transition: none;
  }

  // Drag and drop
  .flip-list-move {
    transition: transform 0.5s;
  }
  .no-move {
    transition: transform 0s;
  }
  &--ghost {
    opacity: 0.5;
  }

  .pa-gantt__task__buttons {
    position: absolute;
    right: 0;
    visibility: hidden;

    .v-btn,
    .v-btn::before,
    .v-btn__content,
    i {
      transition: none;
    }
  }
}
</style>
